<template>
  <fts-dropdown-select 
    :append-to-body="appendToBody" 
    :strategy-fixed="strategyFixed" 
    :placement="placement"
    content-class="sort-content sort-dropdown-menu" 
    :reference-id="referenceId" 
    class="fts-sort" 
    :disabled="isDisabled"
    @show="onShow" 
    @hide="onClose"
  >
    <!-- toggle -->
    <template #toggle>
      <slot 
        name="toggle" 
        :sorted-field="sortedField" 
        :disabled="disabled" 
        :total-items-exceed="totalItemsExceed"
      >
        <fts-tippy
          :content="sortTotalItemsExceed" 
          v-if="totalItemsExceed"
        >
          <fts-btn
            outline 
            text-transform-init
            class="sort-action-button"
            disabled
          >
            <span class="material-symbols-outlined sort-action-button-icon">swap_vert</span>
            <span>Sort</span>
          </fts-btn>
        </fts-tippy>

        <fts-btn
          outline
          text-transform-init
          class="sort-action-button"
          :class="{
            'sort-action-button--active': isValidSorting
          }"
          v-else
        >
          <template v-if="isValidSorting">
            <span class="material-symbols-outlined sort-action-button-icon" v-if="primarySort.direction">arrow_upward</span>
            <span class="material-symbols-outlined sort-action-button-icon" v-else>arrow_downward</span>
            <span>{{ sortField(primarySort).displayName }}</span>
          </template>

          <template v-else>
            <span class="material-symbols-outlined sort-action-button-icon">swap_vert</span>
            <span>Sort</span>
          </template>
        </fts-btn>
      </slot>
    </template>

    <!-- menu -->
    <template #menu>
      <div class="sort-wrapper" v-for="(x, ind) in sorts" :key="x.id">
        <fts-dropdown-select class="sort-info" placement="bottom-start" append-to-body prevent-overflow-alt-axis
          prevent-overflow-tether-off :offset="[0, 6]" :selected-option="x"
          append-content-class="sort-info-content sort-info-dropdown-menu" :reference-for="referenceId"
          @selected="updateSelection($event, x.id)" @show="selectionOnShow(ind)">
          <div slot="toggle" class="sort-toggle">
            <template v-if="sortField(x)">
              <fts-label-group v-if="sortField(x)" :item="{
                ...sortField(x),
                displayName: sortField(x).label || sortField(x).displayName
              }" information :information-modules="modules" emoji :emoji-size="14" />
            </template>
            <template v-else>
              <div class="sort-toggle-name">
                Select sort option
              </div>
            </template>
            <span class="material-symbols-outlined" style="font-size:16px; line-height:1;">keyboard_arrow_down</span>
          </div>

          <!-- menu -->
          <template #menu="{ select, defaultOptionClass }">
            <div class="sort-filter" @click.stop>
              <div class="fts-form-control-filter">
                <input class="fts-form-control" v-model.trim="sortFilter" placeholder="Search something for..."
                  :ref="`selectionFilter-${ind}`" />
                <div class="search-icon">
                  <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                </div>
              </div>
            </div>
            <fts-label-group v-for="col in sortColumns" :key="col.id"
              :item="{ ...col, displayName: col.label || col.displayName }" information :information-modules="modules"
              emoji :emoji-size="14" class="sort-option" :class="defaultOptionClass" @click.native.stop="select(col)" />
          </template>
        </fts-dropdown-select>
        <!-- direction -->
        <div @click="updateDirection(x.id)" class="sort-toggle sort-dir">
          
          {{ x.direction === 0 ? "Ascending" : "Descending" }}
          <span class="material-symbols-outlined" style="font-size:16px; line-height:1;"
            v-if="x.direction">keyboard_arrow_down</span>
          <span class="material-symbols-outlined" style="font-size:16px; line-height:1;" v-else>keyboard_arrow_up</span>
        </div>
      </div>
      <div class="divider" style="margin: 0px; margin-top: 4px;" />
      <fts-btn text text-transform-init class="clear-button" @click="resetSort(primarySort.id)">
        <font-awesome-icon :icon="['far', 'trash-alt']" style="font-size: 0.775rem;" />
        Clear
      </fts-btn>
    </template>
  </fts-dropdown-select>
</template>

<script>
import commons from '../commons/commons';
import queryLogic from '../commons/queryLogic';
import { Sort_Total_Items_Exceed } from '../commons/message';

import FTSBtn from '../elements/FTSBtn';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSTippy from '../elements/FTSTippy';

export default {
  props: {
    modules: Array,
    appendToBody: Boolean,
    strategyFixed: Boolean,
    placement: {
      type: String,
      default: 'bottom'
    },
    fields: {
      type: Array,
      default: () => []
    },
    sort: {
      type: [Object, Array], /* Object deprecated */
      default: () => [{ id: commons.nanoID(), direction: 0 }]
    },
    totalItemsExceed: Boolean,
    disabled: Boolean
  },
  model: {
    prop: "sort",
    event: "updateSort",
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    referenceId: null,
    sortFilter: null,
  }),
  methods: {
    sortField(item) {
      if (this.sortColumns) {
        return this.sortColumns.find(x =>
          (item.fieldId && x.id === item.fieldId) ||
          (!item.fieldId && x.fieldName === item.fieldName)
        );
      }
    },
    updateDirection(id) {
      let sorts = _.cloneDeep(this.sorts);
      let sort = sorts.find(x => x.id === id);
      if (sort) {
        this.$set(sort, "direction", sort.direction == 0 ? 1 : 0);
        this.sorts = sorts;
      }
    },
    updateSelection(option, id) {
      let sorts = _.cloneDeep(this.sorts);
      let sort = sorts.find(x => x.id === id);
      if (sort) {
        if (option.id) {
          sort.fieldName && this.$delete(sort, "fieldName");
          this.$set(sort, "fieldId", option.id);
        } else {
          sort.fieldId && this.$delete(sort, "fieldId");
          this.$set(sort, "fieldName", option.fieldName);
        }
        this.sorts = sorts;
      }
    },
    resetSort(id) {
      let sorts = _.cloneDeep(this.sorts);
      const findIndex = sorts.findIndex(x => x.id === id);
      if (findIndex > -1) {
        this.$set(sorts, findIndex, {
          id: commons.nanoID(),
          direction: 0
        });
        this.sorts = sorts;
      }
    },
    selectionOnShow(ind) {
      this.sortFilter = null;
      this.$nextTick(() => {
        const ref = this.$refs[`selectionFilter-${ind}`];
        ref && ref[0] && ref[0].focus();
      });
    },
    onShow() {
      if (!Boolean(this.sorts && this.sorts.length)) {
        this.sorts = [{ id: commons.nanoID(), direction: 0 }];
      }
    },
    onClose() {
      if (!Boolean(this.sort && this.sort.some(x => x.fieldId || x.fieldName))) {
        this.sorts = null;
      }
      this.$emit("closed");
    },
  },
  computed: {
    isValidSorting(){
      return this.primarySort && this.primarySort.fieldId != null || this.primarySort && this.primarySort.fieldName != null;
    },
    primarySort() {
      if(this.sort) return this.sort[0];
      return null;
    },
    isDisabled() {
      return this.disabled || this.totalItemsExceed;
    },
    sorts: {
      get() {
        if (commons.isObject(this.sort)) { /* Object deprecated */
          this.$emit(
            "updateSort",
            [{
              id: commons.nanoID(),
              fieldId: this.sort.field && this.sort.field.id,
              fieldName: this.sort.fieldName && this.sort.field.fieldName,
              direction: this.sort.direction
            }]
          );
        } else {
          return this.sort;
        }
      },
      set(val) {
        this.$emit('updateSort', val);
      }
    },
    sortColumns() {
      let fields = _.cloneDeep(this.fields);
      const createdByColIndex = fields.findIndex(x => x.id === 'createdBy' && x.system) + 1;
      const lastChangeByColIndex = fields.findIndex(x => x.id === 'lastChangeBy' && x.system) + 1;
      /* created date */
      if (
        createdByColIndex &&
        !fields.some(x => x.id === 'createdDate' && x.system)
      ) {
        const createdDateColumn = queryLogic.querySelectionPrefix.find(
          x => x.id === 'createdDate'
        );

        if (createdDateColumn)
          fields.splice(createdByColIndex, 0, { ...createdDateColumn });
      }
      /* last change date */
      if (
        lastChangeByColIndex &&
        !fields.some(x => x.id === 'lastChangeDate' && x.system)
      ) {
        const lastChangeDateColumn = queryLogic.querySelectionPrefix.find(
          x => x.id === 'lastChangeDate'
        );

        if (lastChangeDateColumn)
          fields.splice(lastChangeByColIndex, 0, { ...lastChangeDateColumn });
      }
      /* exclude */
      fields = fields.filter(x =>
        ![9, 10, 11, 12, 13, 14, 15, 22, 24, 29, 33].some(y => x.type == y) &&
        x.id != 'assignTo'
      );
      /* sort filter */
      if (this.sortFilter)
        return fields.filter(x =>
          x.displayName.toLowerCase().indexOf(this.sortFilter.toLowerCase()) != -1 ||
          (x.label && x.label.toLowerCase().indexOf(this.sortFilter.toLowerCase()) != -1)
        );
      /* default */
      return fields;
    },
    sortedField() {
      const sort = this.sorts && this.sorts[0];
      return (
        sort &&
        (sort.fieldId || sort.fieldName) &&
        { ...this.sortField(sort), direction: sort.direction }
      );
    },
  },
  created() {
    this.sortTotalItemsExceed = Sort_Total_Items_Exceed;
  },
  mounted() {
    this.referenceId = `inistate-ref-${this._uid}`;
  },
};
</script>

<style lang="scss" scoped>
.fts-sort {
  .sort-toggle-btn {
    max-width: 250px;
    min-height: auto;
    height: auto;
    padding-left: 0.625rem;
    padding-right: 0.625rem;
    border: 1px solid transparent;
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    letter-spacing: normal;

    svg {
      margin-left: 0.5rem;
      margin-right: 0;
    }

    &:hover,
    &.sort-toggle-btn--active {
      border-color: var(--fts-form-control-border);
    }
  }
}
</style>
<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-sort {
  max-width: 100%;
  display: inline-flex;
  vertical-align: middle;
}

.sort-toggle {
  @include user-select-none;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--fts-border);
  border-radius: 0.25rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  cursor: pointer;

  .sort-toggle-name {
    @include text-overflow-ellipsis;
  }

  &.sort-dir {
    margin-left: 0.5rem;

    svg {
      margin-right: 0.375rem;
      color: var(--fts-default);
    }
  }

  font-weight: 500;
  color: var(--inistate-text-primary-color);
  border: 1px solid var(--inistate-color-neutral-200);
  justify-content: space-between;
  padding: 8px 12px 8px 12px;
  width: 130px;
}

.sort-action {
  margin-left: 0.375rem;
}

.sort-toggle-disabled-exclamation {
  margin-left: -0.125rem;
  margin-right: 0.25rem;
  font-size: 1.125rem;
  color: var(--fts-disabled);
}

.sort-toggle-disabled-wrapper {
  display: flex;
  align-items: center;

  .sort-toggle-btn.fts-btn.disabled,
  .sort-toggle-disabled-exclamation {
    cursor: default !important;
  }
}

.sort-content.sort-dropdown-menu {
  width: 290px;
  max-width: 360px;
  margin-right: 80px;

  // padding: 0.5rem 0.7rem;
  .sort-wrapper {
    display: flex;
    align-items: center;
    margin: 0.5rem 0.7rem;
  }

  .sort-info {
    flex: 1;
    min-width: 0;
  }
}

.sort-info-content.sort-info-dropdown-menu {
  padding: 0 0 .375rem;

  .sort-filter {
    @include theme() {
      @include backdrop-background($color: theme-get('fts-popover-alt-background'), $blur: 2px);
    }

    position: sticky;
    top: 0;
    padding: 0.75rem 0.575rem calc(0.75rem - 0.375rem);
  }
}

.divider {
  background-color: var(--inistate-color-neutral-200);
}

.clear-button {
  display: flex;
  width: 100%;
  justify-content: flex-start !important; // horizontal alignment
  align-items: start; // vertical alignment
  margin: 4px 0px;
  color: var(--inistate-text-primary-color) !important;
  font-weight: 400 !important;
  font-size: 12px !important;
  border-radius: 0px !important;
  padding-left: 16px !important;
}

.sort-action-button {
  border: 1px solid var(--inistate-color-neutral-200) !important;
  color: var(--inistate-text-primary-color) !important;
  background-color: var(--inistate-color-shade-white) !important;

  .sort-action-button-icon {
    margin-right: 4px;
    font-weight: var(--inistate-font-weight-bold);
    font-size: 16px;
    color: var(--inistate-icon-primary);
  }

  &:hover {
    background-color: var(--inistate-color-neutral-50) !important;
  }

  &.sort-action-button--active {
    background-color: var(--inistate-color-primary-50) !important;
    border: 1px solid var(--inistate-border-primary-active) !important;
    color: var(--inistate-color-primary-500) !important;

    .sort-action-button-icon {
      color: var(--inistate-icon-brand);
    }
  }
}
</style>
